import { VariantType } from 'notistack';
import { useCallback, useMemo, useState } from 'react';
import { SWRConfiguration } from 'swr';

import { Enum, EnumListKey, useAllEnums } from '~/data/enums';
import { AdminWorkspaceRoles } from '~/data/user/role';
import { UseSWROutput, useSWR } from '~/hooks/useSWR';
import {
  BulkApprovalAction,
  approveTraining,
  getEmployeeOncomingTrainings,
} from '~/scenes/MyWorkspace/components/Widgets/components/ApprovalWidget/data';
import {
  ApprovalWidgetData,
  EmployeeTrainings,
  Registration,
} from '~/scenes/MyWorkspace/components/Widgets/components/ApprovalWidget/types';
import { EmployeeShortDto } from '~/services/Http/client.people.generated';
import { useGetEmployeeTeam } from '~/services/Http/hooks';

const GET_EMPLOYEES_ONCOMING_TRAININGS_SWR_KEY = 'getEmployeeOncomingTrainings';

const useGetEmployeesUnapprovedTrainings = (
  props: {
    employeeIds?: number[];
  },
  swrConfig?: SWRConfiguration<EmployeeTrainings[]>,
): UseSWROutput<EmployeeTrainings[]> => {
  const { employeeIds } = props;

  return useSWR(
    !!employeeIds
      ? [GET_EMPLOYEES_ONCOMING_TRAININGS_SWR_KEY, employeeIds]
      : null,
    () =>
      Promise.all(
        (!!employeeIds ? employeeIds : []).map((employeeId) =>
          getEmployeeOncomingTrainings(employeeId).then(
            (result): EmployeeTrainings[] =>
              result
                .filter((t) => t.registration.approvalStatus === 'WAITING')
                .map((t) => ({
                  ...t,
                  employeeId,
                })),
          ),
        ),
      ).then((results) => {
        return results.flat();
      }),
    swrConfig,
  );
};

type Props = {
  widgetRoles?: AdminWorkspaceRoles[];
  showSnackBar: (
    responsesNumber: number,
    variant: VariantType,
    action: BulkApprovalAction,
  ) => void;
  enabled: boolean;
};

type UseApprovalWidgetTrainingDataOutput = {
  isLoading: boolean;
  failedToLoad: boolean;
  data: {
    training: ApprovalWidgetData[];
  };
  onDecision: (action: BulkApprovalAction) => Promise<void>;
  selection: ApprovalWidgetData[];
  setSelection: (selection: ApprovalWidgetData[]) => void;
  isSubmitting: boolean;
};

export const useApprovalWidgetTrainingData = (
  props: Props,
  swrConfig?: SWRConfiguration,
): UseApprovalWidgetTrainingDataOutput => {
  const { widgetRoles, showSnackBar, enabled } = props;

  const { getEnums } = useAllEnums();

  const { internalTrainings, externalTrainings } = useMemo(() => {
    return {
      internalTrainings: getEnums(EnumListKey.INTERNAL_TRAINING_TYPE),
      externalTrainings: getEnums(EnumListKey.EXTERNAL_TRAINING_TYPE),
    };
  }, [getEnums]);

  const {
    loading: isLoadingTeamMembers,
    failedToLoad: failedToLoadTeamMembers,
    data: teamMembers,
  } = useGetEmployeeTeam(
    {
      employeeId: 'me',
      roles: widgetRoles,
    },
    swrConfig,
    !enabled,
  );

  const {
    data: teamMembersTrainings,
    isLoading: isLoadingTrainings,
    failedToLoad: failedToLoadTrainings,
    mutate,
  } = useGetEmployeesUnapprovedTrainings(
    {
      employeeIds: teamMembers
        ? teamMembers.map((tm) => tm.employeeId)
        : undefined,
    },
    swrConfig,
  );

  const trainingWidgetData = useMemo(() => {
    return !!teamMembersTrainings && !!teamMembers
      ? teamMembersTrainings.map((tmt) => {
          const teamMember = teamMembers.find(
            (tm) => tm.employeeId === tmt.employeeId,
          );

          const getType = (
            type: string,
            internalTrainings: Enum[],
            externalTrainings: Enum[],
            classification: {
              class: string;
              group: string;
              type: string;
            },
          ) => {
            switch (classification.group) {
              case 'INTERNAL_TRAINING_TYPE':
                return internalTrainings.find((t) => t.code === type)?.name;
              case 'EXTERNAL_TRAINING_TYPE':
                return externalTrainings.find((t) => t.code === type)?.name;
              default:
                return type;
            }
          };

          return {
            employee: EmployeeShortDto.fromJS({
              ...teamMember,
              id: tmt.employeeId,
            }),
            type: getType(
              tmt.training.classification.type,
              internalTrainings || [],
              externalTrainings || [],
              tmt.training.classification,
            ),
            startDate: tmt.training.startDate,
            hours: tmt.training.hours,
            uid: tmt.registration.id,
            info: '',
          } as ApprovalWidgetData;
        })
      : [];
  }, [teamMembersTrainings, teamMembers, internalTrainings, externalTrainings]);

  const [selectedTrainings, setSelectedTrainings] = useState<
    ApprovalWidgetData[]
  >([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleTrainingsApprove = useCallback(
    async (
      action: BulkApprovalAction,
      selectedTrainings: EmployeeTrainings[],
    ) =>
      await Promise.all(
        selectedTrainings.map(async (training) => {
          const registration: Registration = {
            ...training.registration,
            approvalStatus:
              action === BulkApprovalAction.APPROVE ? 'APPROVED' : 'REJECTED',
          };
          const registrationResponse = await approveTraining(
            training.employeeId,
            registration,
          );

          return {
            ...registrationResponse,
          };
        }),
      ),
    [],
  );

  const handleTrainingsDecision = useCallback(
    async (action: BulkApprovalAction): Promise<void> => {
      const successResponsesNumber = selectedTrainings.length;

      const selectedTeamMembersTrainings =
        teamMembersTrainings?.filter((t) =>
          selectedTrainings.some((rqt) => rqt.uid === t.registration.id),
        ) ?? [];

      if (!selectedTrainings || !selectedTeamMembersTrainings) {
        return Promise.resolve();
      }

      setIsSubmitting(true);
      return handleTrainingsApprove(action, selectedTeamMembersTrainings)
        .then(() => {
          mutate();
          setSelectedTrainings([]);
          showSnackBar(successResponsesNumber, 'success', action);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    },
    [
      selectedTrainings,
      setSelectedTrainings,
      teamMembersTrainings,
      handleTrainingsApprove,
      showSnackBar,
      mutate,
      setIsSubmitting,
    ],
  );

  return {
    isLoading: isLoadingTeamMembers || isLoadingTrainings,
    failedToLoad: failedToLoadTeamMembers || failedToLoadTrainings,
    data: {
      training: trainingWidgetData,
    },
    onDecision: handleTrainingsDecision,
    selection: selectedTrainings,
    setSelection: setSelectedTrainings,
    isSubmitting,
  };
};
